import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CommentRow from './CommentRow';
import { orderByAnchors, useCommentAnchors } from './commentAnchors';
import { mentionsViewer } from './mentions';
import { ReviewComment } from './types';

/**
 * The findings list, inside the viewer's rail (2026-08-26, Fredrik).
 *
 * This replaces the "Comment Summary" / "All Comments" sections that sat
 * BELOW the document. Same reasoning as the consultations move that preceded
 * it: reading a finding and looking at the passage it is about are one act,
 * and a list several screens below the content forces you to do them one at a
 * time, from memory.
 *
 * THE HOST STILL OWNS THE CARD. `renderDetail` is what a row expands into,
 * and each host passes its own: ReviewInterface passes the reviewer card
 * (resolve, reply, edit), DecisionView passes the one with the disposition
 * control, PreScreenView passes a read-only one. Nothing about resolve,
 * disposition, the per-item confirmation gate or the reply rules is
 * reimplemented here — this component owns the LIST, not the card.
 *
 * SPY. `visibleCommentIds` comes from the format's anchor adapter, so this
 * code never learns whether it is looking at a docx, a snapshot or a video.
 * The adapter reports everything on screen; this list marks and opens only
 * the FIRST of them — the reading position. Marking all of them lit the whole
 * rail up on a short document and said nothing (measured on a 3-page pdf: all
 * three rows active at once, at every scroll offset).
 *
 * The reading-position row opens itself as you scroll (Fredrik, 2026-08-26),
 * so the comment about the passage under your eye is the one you can read.
 * One row at a time keeps that from turning the rail into a wall.
 *
 * ORDER. The list is sorted by where each anchor sits in the content, not by
 * the order the API returned (Fredrik, 2026-08-26). Comments come back newest
 * or oldest first depending on the route, so scrolling the document made the
 * highlight jump 1 -> 3 -> 2 down a rail that was itself standing still. The
 * order comes from the adapter — `orderedCommentIds` — for the same reason
 * `reveal` does: only the format knows what "further down" means. An empty
 * array (adapter cannot order, markers not yet rendered, inline comments
 * switched off) leaves the API order untouched rather than inventing one.
 */

interface CommentsRailProps {
  comments: ReviewComment[];
  viewerEmail?: string | null;
  /** What a row expands into. */
  renderDetail: (comment: ReviewComment) => React.ReactNode;
  /** Counters / bulk actions, rendered above the list. */
  header?: React.ReactNode;
  emptyText?: string;
  /** Set by the panel when a marker click asks for a specific comment. */
  openId?: string | null;
  onOpenIdHandled?: () => void;
}

const SEVERITY_ACCENT: Record<string, string> = {
  critical: '#dc3545',
  major: '#fd7e14',
  minor: '#ffc107',
  observation: '#6c757d'
};

/**
 * Scroll a row into view inside the rail's own scroller, never the page.
 *
 * The open row goes to the TOP of the scroller, not the middle (2026-08-26,
 * Fredrik: "only three lines of text is visible ... very little context is in
 * view before you need to scroll"). Centring a tall open card spends half the
 * column on the collapsed rows above it and pushes the card's own text below
 * the fold; aligning its top hands the whole remaining height to the thing
 * the reader just asked to read. A small margin keeps it from touching the
 * header.
 */
const ROW_TOP_MARGIN = 8;

/**
 * Put a row at the top of the rail, or — when we know where the passage
 * ended up — BESIDE it.
 *
 * `anchorViewportTop` is the passage's real position on screen after the
 * content scroll settled. Passing null keeps the original behaviour, which
 * is still what the spy-driven path wants: it opens a row because the reader
 * scrolled to that passage themselves, so there is nothing to jump to and
 * moving the rail to chase it would fight the scroll.
 *
 * Clamping is the whole reason this is measured. Both scrollers run out at
 * their ends, so alignment is a request, not a guarantee; Math.max(0, …)
 * degrades it to "as close as this rail can get", which is what Word does
 * when a comment is near the top of a document.
 */
function alignRowToAnchor(el: HTMLElement, anchorViewportTop: number | null): void {
  let scroller: HTMLElement | null = el.parentElement;
  while (scroller) {
    const overflowY = window.getComputedStyle(scroller).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
    scroller = scroller.parentElement;
  }
  if (!scroller) return;

  let top = el.offsetTop - scroller.offsetTop - ROW_TOP_MARGIN;
  if (anchorViewportTop !== null) {
    // How far the row currently sits from where the passage is, in viewport
    // terms; scrolling the rail by that closes the gap.
    const delta = el.getBoundingClientRect().top - anchorViewportTop;
    top = scroller.scrollTop + delta;
  }
  scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

const CommentsRail: React.FC<CommentsRailProps> = ({
  comments, viewerEmail, renderDetail, header, emptyText = 'No comments yet.',
  openId, onOpenIdHandled
}) => {
  const {
    reveal, setActive, visibleCommentIds, orderedCommentIds, hasAnchor,
    setHovered, hoveredCommentId, anchorRect
  } = useCommentAnchors();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /**
   * Ignore the spy for a moment after a click.
   *
   * Clicking a row scrolls the CONTENT to it, and that scroll drags other
   * anchors through the reading position on the way — so without this the
   * row you just opened closes again and a row you were only passing over
   * opens instead, which reads as the rail fighting you.
   */
  const suppressSpyUntil = useRef(0);

  /**
   * One row open at a time — an accordion is the difference between a list
   * you can scan and a wall you scroll.
   *
   * A CLICK ALWAYS TAKES YOU THERE, and never closes (2026-08-26, Fredrik:
   * "the comments do not link to the place in the text where the comment
   * exists"). It looked broken for a reason that is invisible from outside:
   * the spy OPENS the reading-position row by itself, so the row the reader
   * reaches for is usually already open — and the old toggle read that click
   * as "close", which returns early and scrolls nowhere. The jump worked
   * perfectly; you just could not get at it.
   *
   * Nothing is lost by dropping click-to-close: the accordion closes this row
   * when another opens, and the spy re-opens the reading-position row on the
   * next scroll anyway, so "closed" was never a state a reader could keep.
   *
   * reveal() also moved OUT of the setState updater. A side effect inside a
   * reducer runs whenever React chooses to call it, which is not a promise
   * about how many times.
   */
  const toggle = useCallback((id: string) => {
    suppressSpyUntil.current = Date.now() + 900;
    const revealed = reveal(id);
    setExpandedId(id);
    /**
     * LINE THE ROW UP WITH THE PASSAGE (2026-08-27, Fredrik: "I'd like the
     * highlighted text to appear next to the comment in the rail").
     *
     * Measured after the fact rather than both sides agreeing on a fraction
     * of the viewport in advance, because both scrolls CLAMP: a comment near
     * the top of the document cannot be scrolled up to the reading band, and
     * a fixed target would then put the row at the band while the passage sat
     * wherever the document ran out. Reading the anchor's real rect makes the
     * common case — the first comment in a document — the one it gets right.
     *
     * The delay is the smooth scroll settling; there is no portable event for
     * it, and the same 320-340ms wait is already how the snapshot adapter
     * times its flash.
     */
    if (!revealed) return;
    window.setTimeout(() => {
      const row = listRef.current?.querySelector<HTMLElement>(`[data-comment-row="${id}"]`);
      const rect = anchorRect(id);
      if (row) alignRowToAnchor(row, rect ? rect.top : null);
    }, 340);
  }, [reveal, anchorRect]);

  // A marker click in the content asks for a specific row.
  useEffect(() => {
    if (!openId) return;
    if (!comments.some(c => c._id === openId)) return;
    setExpandedId(openId);
    onOpenIdHandled?.();
  }, [openId, comments, onOpenIdHandled]);

  // Document order — see orderByAnchors. Memoised because it rebuilds the
  // whole list, and the spy re-renders this component on every scroll.
  const ordered = useMemo(
    () => orderByAnchors(comments, c => c._id, orderedCommentIds),
    [comments, orderedCommentIds]);

  // The reading position: the first anchor on screen that belongs to this
  // list. This is what gets marked and what opens itself.
  const leadVisible = visibleCommentIds.find(id => comments.some(c => c._id === id)) || null;

  useEffect(() => {
    if (!leadVisible) return;
    if (Date.now() < suppressSpyUntil.current) return;
    setExpandedId(leadVisible);
  }, [leadVisible]);

  // Keep whatever is open where the reader can see it, in the RAIL's own
  // scroller — never the page, which would move the document under them.
  useEffect(() => {
    if (!expandedId) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-comment-row="${expandedId}"]`);
    if (el) alignRowToAnchor(el, null);
  }, [expandedId]);

  /**
   * Mark the open row's anchor in the content, so the pair reads as one
   * thing (Fredrik, 2026-08-26). Cleared on unmount — switching tabs or
   * formats must not leave a highlight behind on an element nobody is
   * pointing at any more.
   */
  useEffect(() => {
    if (!comments.some(c => c._id === expandedId)) return;
    setActive(expandedId);
  }, [expandedId, comments, setActive]);

  useEffect(() => () => setActive(null), [setActive]);

  return (
    <div data-testid="comments-rail">
      {header}
      {ordered.length === 0 ? (
        <p data-testid="comments-rail-empty" className="rail-empty">{emptyText}</p>
      ) : (
        <div ref={listRef} className="rail-list">
          {ordered.map(c => {
            const forMe = mentionsViewer(c, viewerEmail);
            return (
              <CommentRow
                key={c._id}
                commentId={c._id}
                author={c.reviewer?.name || 'Unknown'}
                at={c.createdAt}
                quote={c.textSelection || c.sectionContent || null}
                body={c.issue || ''}
                accent={SEVERITY_ACCENT[c.severity || ''] || undefined}
                forMe={forMe}
                active={c._id === expandedId}
                hovered={c._id === hoveredCommentId}
                hasAnchor={hasAnchor(c._id)}
                expanded={expandedId === c._id}
                onToggle={() => toggle(c._id)}
                onHoverChange={h => setHovered(h ? c._id : null)}
                badges={
                  <>
                    {c.severity && <span className={`severity-badge ${c.severity}`}>{c.severity}</span>}
                    {forMe && !c.resolved && <span className="rail-badge-mention">Mentions you</span>}
                    {c.resolved && <span className="rail-badge-resolved">Resolved</span>}
                  </>
                }
              >
                {renderDetail(c)}
              </CommentRow>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommentsRail;
